// React DX layer for Amplitude Experiment.
//
//   <ExperimentProvider>            wrap the app once; subscribes to the singleton
//   useExperiment(flagKey)          → { value, isControl, payload, ready }
//   <Experiment flag="…"> + <Variant value="…"> + <Fallback>
//                                   declarative variant rendering with safe fallback
//
// Reads from the singleton client in client.ts. Never triggers a fetch on
// render — variants are bootstrapped once at app boot via initExperimentClient().
//
// Exposure: useExperiment() / <Experiment> trigger one `.variant()` call per
// mount, which fires the `$exposure` event. Render stays side-effect free:
// assignment reads use peekVariant(), and the single exposure call happens in
// an effect. QA overrides skip exposure entirely (see client.ts).

import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getVariant,
  isExperimentClientReady,
  peekVariant,
  subscribeToExperiments,
} from './client';

interface ExperimentContextValue {
  ready: boolean;
}

const ExperimentContext = createContext<ExperimentContextValue>({ ready: false });

/**
 * Subscribes to singleton ready state and re-renders consumers when variants
 * resolve. Place once near the app root.
 *
 * The initial state reads from the singleton synchronously, so consumers that
 * mount after start() resolves render correctly on the first paint.
 */
export function ExperimentProvider({ children }: { children: ReactNode }): ReactElement {
  const [ready, setReady] = useState<boolean>(() => isExperimentClientReady());
  useEffect(() => {
    if (isExperimentClientReady()) {
      setReady(true);
      return;
    }
    const unsubscribe = subscribeToExperiments(() => {
      setReady(isExperimentClientReady());
    });
    return unsubscribe;
  }, []);
  const value = useMemo<ExperimentContextValue>(() => ({ ready }), [ready]);
  return <ExperimentContext.Provider value={value}>{children}</ExperimentContext.Provider>;
}

export interface UseExperimentResult {
  /** The variant value ('control', 'treatment_a', etc.). 'off' when not assigned or not ready. */
  value: string;
  /** True when value === 'control'. Convenience for the common case. */
  isControl: boolean;
  /** Optional JSON payload attached to the variant in Amplitude. */
  payload: unknown;
  /** True once the SDK has resolved. Use this to gate render if you want to suppress flicker. */
  ready: boolean;
}

/**
 * Read a variant for one flag. Triggers `$exposure` once on mount (via the
 * SDK's automatic exposure tracking + its own dedup window).
 */
export function useExperiment(flagKey: string): UseExperimentResult {
  const { ready } = useContext(ExperimentContext);
  const exposedFlagRef = useRef<string | null>(null);

  // Re-read every render is cheap (it's a map lookup), but render must stay
  // pure. peekVariant() reads the current assignment without firing exposure.
  const variant = ready ? peekVariant(flagKey) : { value: 'off', payload: undefined };

  useEffect(() => {
    if (!ready || exposedFlagRef.current === flagKey) return;
    exposedFlagRef.current = flagKey;
    try {
      getVariant(flagKey);
    } catch {
      exposedFlagRef.current = null;
    }
  }, [flagKey, ready]);

  return {
    value: variant.value ?? 'off',
    isControl: variant.value === 'control',
    payload: variant.payload,
    ready,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Declarative API: <Experiment> / <Variant> / <Fallback>
// ───────────────────────────────────────────────────────────────────────────

interface VariantProps {
  value: string;
  children: ReactNode;
}

/**
 * Marker component — must be a direct child of <Experiment>. Renders its
 * children when the assigned variant equals `value`.
 */
export function Variant(_props: VariantProps): null {
  return null;
}
Variant.displayName = 'Experiment.Variant';

interface FallbackProps {
  children: ReactNode;
}

/**
 * Marker component — must be a direct child of <Experiment>. Renders its
 * children when no <Variant> matches, when the flag is off, or when the SDK
 * is still resolving.
 */
export function Fallback(_props: FallbackProps): null {
  return null;
}
Fallback.displayName = 'Experiment.Fallback';

interface ExperimentProps {
  flag: string;
  children: ReactNode;
}

/**
 * Declarative variant gate.
 *
 *   <Experiment flag="welcome_screen_v1">
 *     <Variant value="control"><ControlBlock /></Variant>
 *     <Variant value="treatment_a"><TreatmentBlock /></Variant>
 *     <Fallback><ControlBlock /></Fallback>
 *   </Experiment>
 *
 * - When the SDK is not yet ready → renders <Fallback>.
 * - When the assigned variant matches a <Variant value="…"> → renders that branch.
 * - Otherwise → renders <Fallback>.
 *
 * Triggers exposure once per mount.
 */
export function Experiment({ flag, children }: ExperimentProps): ReactElement | null {
  const { value, ready } = useExperiment(flag);

  let matched: ReactElement | null = null;
  let fallback: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Variant) {
      const props = child.props as VariantProps;
      if (props.value === value) {
        matched = cloneElement(child, { children: props.children });
      }
    } else if (child.type === Fallback) {
      const props = child.props as FallbackProps;
      fallback = cloneElement(child, { children: props.children });
    }
  });

  if (!ready) return renderChildren(fallback);
  if (matched) return renderChildren(matched);
  return renderChildren(fallback);
}

function renderChildren(el: ReactElement | null): ReactElement | null {
  if (!el) return null;
  const props = el.props as { children?: ReactNode };
  return <>{props.children}</>;
}
